import {
  BmExpGroup,
  BmItem,
  BmMgmFeeGroup,
  BmMgmFeeSubGroupWrapper,
  BmPeriodEnum,
  BudgetModel,
  BudgetModelInvestment,
} from '../../pojo/bm-classes';
import { BmCalcType, BmDataFillingType } from '../../pojo/bm-enums';
import { DEFAULTS, MG_FEE_SUBGROUPS, DEFAULT_OP_EXPENSES, DEFAULT_ORG_EXPENSES } from "../../pojo/bm-const";

export class BmDefaultFormCreatorService {
  /**
   * Create a new BudgetModel with `yearsNumber` .
   */
  public static createNewBudgetModelDefault(): BudgetModel {
    return {
      uuid: null,
      orgUuid: null,
      name: 'Budget model',
      totalCommitment: DEFAULTS.TOTAL_COMMITMENT,
      yearsNumber: DEFAULTS.YEARS_NUMBER,
      lastModifiedDate: null,
      createdDate: null,
      createdBy: null,
      modelData: {

        mgmFee: [
          this.createDefaultMgmFeeGroup('Domestic LPs', DEFAULTS.YEARS_NUMBER, 0, true),
          this.createDefaultMgmFeeGroup('Foreign LPs', DEFAULTS.YEARS_NUMBER, 1, false),
        ],
        operatingExpenses: DEFAULT_OP_EXPENSES.map(([name, std, type], index) =>
          this.createExpensesGroup(name, std, type, DEFAULTS.YEARS_NUMBER, index)),
        organizationalExpenses: DEFAULT_ORG_EXPENSES.map(([name, std, type], index) =>
          this.createExpensesGroup(name, std, type, DEFAULTS.YEARS_NUMBER, index)),
        investment: this.createDefaultInvestment(DEFAULTS.YEARS_NUMBER),
      },
    };
  }

  public static createDefaultMgmFeeGroup(name: string, years: number, priority: number, isVatSelected = true): BmMgmFeeGroup {
    const wrapper = new BmMgmFeeSubGroupWrapper();


    for (const cfg of MG_FEE_SUBGROUPS) {
      const items = this.createItemsArray(years).map((i) => ({
        ...i,
        itemValue: this.calculateItemValue(i.period.type, cfg),
        calcType: BmCalcType.GENERATED,
      }));

      const selected =
        cfg.key === 'vatPcnt' ? isVatSelected : Boolean(cfg.isSelected);

      wrapper[cfg.key] = {
        standardValue: cfg.std,
        isSelected: selected,
        items,
      };
    }
    wrapper.isCatchUpSelected = true;

    return {
      uuid: null,
      name,
      priority,
      subGroups: wrapper,
    };
  }

  static createExpensesGroup(
    name: string,
    std: number,
    type: BmDataFillingType,
    years: number,
    priority: number
  ): BmExpGroup {
    const map: Record<BmDataFillingType, [(v: number) => number, (v: number) => number]> = {
      [BmDataFillingType.YEARLY]: [(v): number => v / 4, (v): number => v],
      [BmDataFillingType.QUARTERLY]: [(v): number => v, (v): number => v * 4],
      [BmDataFillingType.MONTHLY]: [(v): number => v * 3, (v): number => v * 12],
      [BmDataFillingType.MANUAL]: [(): number => 0, (): number => 0],
    };
    const [qFn, yFn] = map[type];

    return {
      uuid: null,
      name,
      priority,
      dataFillingType: type,
      standardValue: std,
      items: this.createItemsArray(years).map((i) => ({
        ...i,
        itemValue: i.period.type === BmPeriodEnum.QUARTER ? qFn(std) : yFn(std),
        calcType: BmCalcType.GENERATED,
      })),
    };
  }

  static createDefaultInvestment(
    years: number,
  ): BudgetModelInvestment {
    const items = this.createItemsArray(years);
    return {
      uuid: null,
      investmentValue: DEFAULTS.INVESTMENT,
      itemsPcnt: items,
      itemsValue: items.map((i) => ({ ...i, itemValue: 0 })),
    };
  }

  public static createItemsArray(yearsNumber: number): BmItem[] {
    const items: BmItem[] = [];
    for (let y = 1; y <= yearsNumber; y++) {
      for (let q = 1; q <= 4; q++) {
        items.push(BmItem.create(0, y, q));
      }
      items.push(BmItem.create(0, y, null));
    }
    return items;
  }

  private static calculateItemValue(periodType: BmPeriodEnum, cfg: {key: string, std: number}): number {
    if (periodType === BmPeriodEnum.QUARTER && cfg.key === 'mgmFeesPcnt') {
      return cfg.std / 4;
    }
    return cfg.std;
  }
}
